import { differenceInCalendarDays, format } from 'date-fns'
import { TZDate } from '@date-fns/tz'
import { create } from '@bufbuild/protobuf'
import { TimestampSchema, type Timestamp } from '@bufbuild/protobuf/wkt'

/**
 * Wraps a Date in a TZDate so date-fns format calls render wall-clock
 * values in the given IANA timezone. Returns the original Date when no
 * timezone is provided so existing call sites remain viewer-local.
 */
function toTZDate(date: Date, timeZone?: string): Date {
  if (!timeZone) return date
  return new TZDate(date.getTime(), timeZone)
}

// Detects whether a date-fns format string renders a time-of-day component.
// Match hour tokens only (h, H, k, K). Minute/second/am-pm tokens are not
// matched: `m` collides with `MMM`, `s` collides with date words, and `a`
// without an hour is not a meaningful time. Hour detection alone is sufficient
// because every time-bearing format we use pairs an hour with its minute.
function hasTimeComponent(formatString: string): boolean {
  return /[hHkK]/.test(formatString)
}

// Locale is intentionally pinned to 'en-US' — we want stable abbreviations
// ("CST", "PST", "JST", "GMT", "BST") regardless of the browser locale.
// Using the user's locale would return translated long-form values
// (e.g. "heure normale du Centre" in fr-FR), breaking the UI label format.
export function getTimeZoneAbbreviation(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(date)
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
}

/**
 * Converts a JavaScript Date to a protobuf Timestamp
 *
 * @param date - JavaScript Date object
 * @returns Protobuf Timestamp object
 *
 * @example
 * const date = new Date('2024-12-18')
 * const timestamp = dateToTimestamp(date)
 * // Returns: Timestamp { seconds: 1734480000n, nanos: 0 }
 */
export function dateToTimestamp(date: Date): Timestamp | undefined {
  // Check for valid date
  if (!date || isNaN(date.getTime())) {
    return undefined
  }

  const milliseconds = date.getTime()
  const seconds = Math.floor(milliseconds / 1000)
  const nanos = (milliseconds % 1000) * 1000000

  return create(TimestampSchema, {
    seconds: BigInt(seconds),
    nanos: nanos,
  })
}

/**
 * Converts a calendar date (year/month/day only) to a protobuf Timestamp
 * anchored at UTC midnight of that day.
 *
 * Use this — NOT `dateToTimestamp` — for date-only fields (e.g. a task due
 * date) that are persisted in a Postgres `date` column. `dateToTimestamp`
 * encodes the local-midnight *instant*, whose UTC calendar day can differ from
 * the day the user picked (e.g. picking "Dec 18" in UTC+13 yields a Dec 17 UTC
 * instant). A `date` column then stores the wrong day, and on readback it comes
 * back as midnight UTC, which renders as the previous day in any behind-UTC
 * zone. Anchoring at UTC midnight of the picked calendar day keeps the round
 * trip timezone-stable. Pair with {@link timestampToDateOnly} on read.
 *
 * @param date - JavaScript Date; only its local year/month/day are used
 * @returns Protobuf Timestamp at UTC midnight of that calendar day
 */
export function dateToDateOnlyTimestamp(date: Date): Timestamp | undefined {
  if (!date || isNaN(date.getTime())) {
    return undefined
  }

  const utcMidnightMs = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())

  return create(TimestampSchema, {
    seconds: BigInt(utcMidnightMs / 1000),
    nanos: 0,
  })
}

/**
 * Converts a date-only protobuf Timestamp back to a local Date at midnight of
 * the timestamp's UTC calendar day.
 *
 * This is the read-side counterpart to {@link dateToDateOnlyTimestamp}. Backend
 * `date` columns serialize to a Timestamp at midnight UTC; interpreting that
 * instant in the viewer's local zone (the default for `new Date(seconds*1000)`
 * and the format helpers) shifts the day backward for anyone behind UTC. By
 * reading the UTC calendar day and rebuilding a *local* midnight Date, the day
 * the user picked is preserved regardless of timezone, and the value can be fed
 * to the local-rendering format helpers or compared against `new Date()` safely.
 *
 * @param timestamp - Protobuf timestamp (or anything with a `seconds` field)
 * @returns Local Date at midnight of the UTC calendar day, or undefined if invalid
 */
export function timestampToDateOnly(timestamp?: any): Date | undefined {
  if (!timestamp) return undefined

  const seconds = typeof timestamp.seconds === 'bigint'
    ? Number(timestamp.seconds)
    : timestamp.seconds

  if (seconds == null || (typeof seconds === 'number' && isNaN(seconds))) {
    return undefined
  }

  const utc = new Date(seconds * 1000)
  if (isNaN(utc.getTime())) return undefined

  return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate())
}

/**
 * Interprets a picked calendar date + time-of-day as wall-clock in the given
 * IANA timezone and returns the matching UTC instant as a protobuf Timestamp.
 *
 * Use for timezone-aware time inputs — e.g. scheduling a shift against an event
 * in the event's own zone — so the stored instant is correct regardless of the
 * editor's browser zone. Falls back to the viewer's local zone when `timeZone`
 * is empty, preserving the prior local-time behavior.
 *
 * Inverse of {@link timestampToZonedInputs}.
 *
 * @param date - JavaScript Date; only its local year/month/day are used
 * @param hours - Hour of day (0-23) in the target timezone
 * @param minutes - Minute of hour (0-59) in the target timezone
 * @param timeZone - Optional IANA timezone (e.g. "America/Los_Angeles")
 */
export function zonedWallClockToTimestamp(
  date: Date,
  hours: number,
  minutes: number,
  timeZone?: string,
): Timestamp | undefined {
  if (!date || isNaN(date.getTime())) return undefined

  if (!timeZone) {
    const local = new Date(date)
    local.setHours(hours, minutes, 0, 0)
    return dateToTimestamp(local)
  }

  // TZDate.tz builds the instant for the given wall-clock in `timeZone`,
  // resolving the correct UTC offset (incl. DST) at that instant.
  const zoned = TZDate.tz(
    timeZone,
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
  )
  return dateToTimestamp(new Date(zoned.getTime()))
}

/**
 * Splits a UTC-instant Timestamp into the calendar date + "HH:mm" time-of-day
 * as they appear in the given IANA timezone, for pre-filling date/time inputs.
 *
 * The returned `date` is a viewer-local Date at midnight of the zoned calendar
 * day (safe to feed to a DatePicker, which reads local year/month/day). Falls
 * back to the viewer's local zone when `timeZone` is empty.
 *
 * Inverse of {@link zonedWallClockToTimestamp}.
 *
 * @param timestamp - Protobuf timestamp (or Date); the UTC instant to split
 * @param timeZone - Optional IANA timezone to render the wall-clock in
 */
export function timestampToZonedInputs(
  timestamp?: any,
  timeZone?: string,
): { date: Date; time: string } | undefined {
  if (!timestamp) return undefined

  let instant: Date
  if (timestamp instanceof Date) {
    instant = timestamp
  } else {
    const seconds = typeof timestamp.seconds === 'bigint'
      ? Number(timestamp.seconds)
      : timestamp.seconds
    if (seconds == null || (typeof seconds === 'number' && isNaN(seconds))) {
      return undefined
    }
    instant = new Date(seconds * 1000)
  }
  if (isNaN(instant.getTime())) return undefined

  const zoned = timeZone ? new TZDate(instant.getTime(), timeZone) : instant
  const date = new Date(zoned.getFullYear(), zoned.getMonth(), zoned.getDate())
  const time = `${String(zoned.getHours()).padStart(2, '0')}:${String(zoned.getMinutes()).padStart(2, '0')}`
  return { date, time }
}

/**
 * Formats a protobuf timestamp to a human-readable date string
 * 
 * @param timestamp - Protobuf timestamp object with seconds property, or a Date object
 * @param formatString - Optional date-fns format string (default: 'MMM d, yyyy')
 * @returns Formatted date string, or empty string if timestamp is invalid
 * 
 * @example
 * formatTimestamp({ seconds: 1734567890, nanos: 0 })
 * // Returns: "Dec 18, 2024"
 * 
 * @example
 * formatTimestamp({ seconds: 1734567890, nanos: 0 }, 'yyyy-MM-dd')
 * // Returns: "2024-12-18"
 */
export function formatTimestamp(
  timestamp?: any,
  formatString: string = 'MMM d, yyyy',
  timeZone?: string,
): string {
  if (!timestamp) return ''

  let date: Date
  if (timestamp instanceof Date) {
    if (isNaN(timestamp.getTime())) return ''
    date = timestamp
  } else {
    const seconds = typeof timestamp.seconds === 'bigint'
      ? Number(timestamp.seconds)
      : timestamp.seconds

    if (seconds == null || (typeof seconds === 'number' && isNaN(seconds))) {
      return ''
    }

    date = new Date(seconds * 1000)
    if (isNaN(date.getTime())) return ''
  }

  const zoned = toTZDate(date, timeZone)
  const base = format(zoned, formatString)
  if (timeZone && hasTimeComponent(formatString)) {
    return `${base} ${getTimeZoneAbbreviation(date, timeZone)}`
  }
  return base
}

/**
 * Formats a protobuf timestamp to include date and time (MMM d, yyyy hh:mm a)
 *
 * @param timestamp - Protobuf timestamp object with seconds property, or a Date object
 * @returns Formatted date string with time in 12-hour format
 *
 * @example
 * formatTimestampWithTime({ seconds: 1734567890, nanos: 0 })
 * // Returns: "Dec 18, 2024 01:31 PM"
 */
export function formatTimestampWithTime(timestamp?: any, timeZone?: string): string {
  return formatTimestamp(timestamp, 'MMM d, yyyy hh:mm a', timeZone)
}

/**
 * Formats a protobuf timestamp to a short date string (MMM d)
 *
 * @param timestamp - Protobuf timestamp object with seconds property, or a Date object
 * @returns Formatted short date string
 */
export function formatShortDate(timestamp?: any, timeZone?: string): string {
  return formatTimestamp(timestamp, 'MMM d', timeZone)
}

/**
 * Formats a protobuf timestamp to a full date string (MMMM d, yyyy)
 * 
 * @param timestamp - Protobuf timestamp object with seconds property, or a Date object
 * @returns Formatted full date string
 */
export function formatFullDate(timestamp?: any, timeZone?: string): string {
  return formatTimestamp(timestamp, 'MMMM d, yyyy', timeZone)
}

/**
 * Formats a protobuf timestamp to a date and time string
 * 
 * @param timestamp - Protobuf timestamp object with seconds property, or a Date object
 * @returns Formatted date and time string
 */
export function formatDateTime(timestamp?: any, timeZone?: string): string {
  return formatTimestamp(timestamp, 'MMM d, yyyy h:mm a', timeZone)
}

/**
 * Formats a protobuf timestamp with duration to show a time range
 * Format: "MMM d, yyyy h:mm a - h:mm a" (e.g., "Feb 15, 2026 2:00 PM - 4:00 PM")
 * 
 * @param timestamp - Protobuf timestamp object with seconds property, or a Date object (start time)
 * @param durationHours - Duration in hours to add to the start time
 * @returns Formatted date and time range string, or formatted date only if no duration
 */
export function formatDateTimeRange(
  timestamp?: any,
  durationHours?: number | null,
  timeZone?: string,
): string {
  if (!timestamp) return ''

  let startDate: Date
  if (timestamp instanceof Date) {
    if (isNaN(timestamp.getTime())) return ''
    startDate = timestamp
  } else {
    const seconds = typeof timestamp.seconds === 'bigint'
      ? Number(timestamp.seconds)
      : timestamp.seconds

    if (seconds == null || (typeof seconds === 'number' && isNaN(seconds))) {
      return ''
    }
    startDate = new Date(seconds * 1000)
  }

  if (isNaN(startDate.getTime())) {
    return ''
  }

  if (typeof durationHours !== 'number' || isNaN(durationHours) || durationHours <= 0) {
    const zonedStart = toTZDate(startDate, timeZone)
    const datePart = format(zonedStart, 'MMM d, yyyy h:mm a')
    if (timeZone) {
      return `${datePart} ${getTimeZoneAbbreviation(startDate, timeZone)}`
    }
    return datePart
  }

  const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000)

  // Wrap BOTH start and end in TZDate after computing end — this handles
  // DST boundaries correctly because each wall-clock rendering uses the
  // offset at its own instant.
  const zonedStart = toTZDate(startDate, timeZone)
  const zonedEnd = toTZDate(endDate, timeZone)

  const datePart = format(zonedStart, 'MMM d, yyyy')
  const startTime = format(zonedStart, 'h:mm a')
  const endTime = format(zonedEnd, 'h:mm a')

  if (timeZone) {
    return `${datePart} ${startTime} - ${endTime} ${getTimeZoneAbbreviation(endDate, timeZone)}`
  }
  return `${datePart} ${startTime} - ${endTime}`
}

/**
 * Formats a date relative to now (e.g., "Just now", "5m ago", "2h ago")
 *
 * @param date - JavaScript Date object
 * @returns Relative time string
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 5 * 60 * 1000))
 * // Returns: "5m ago"
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Formats a date relative to now with full words (e.g., "just now", "5 mins ago", "2 hours ago")
 *
 * @param date - JavaScript Date object
 * @returns Relative time string with full words
 *
 * @example
 * formatRelativeTimeLong(new Date(Date.now() - 5 * 60 * 1000))
 * // Returns: "5 mins ago"
 */
export function formatRelativeTimeLong(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 1) return 'just now'
  if (diffMinutes === 1) return '1 min ago'
  if (diffMinutes < 60) return `${diffMinutes} mins ago`
  if (diffHours === 1) return '1 hour ago'
  if (diffHours < 24) return `${diffHours} hours ago`
  if (diffDays === 1) return '1 day ago'
  if (diffDays < 7) return `${diffDays} days ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Formats a future-leaning calendar-day relative label: "today", "tomorrow",
 * "in {N} days" for future dates, "yesterday" for one day back, and
 * "{N} days ago" further in the past. Compares calendar days (not 24h
 * windows), so it's stable regardless of time-of-day.
 *
 * @param timestamp - Protobuf timestamp object with seconds property, or a Date object
 * @returns Relative day label, or empty string if timestamp is invalid
 *
 * @example
 * formatRelativeDayLabel(dateToTimestamp(addDays(new Date(), 43)))
 * // Returns: "in 43 days"
 */
export function formatRelativeDayLabel(timestamp?: any): string {
  if (!timestamp) return ''

  let date: Date
  if (timestamp instanceof Date) {
    date = timestamp
  } else {
    const seconds = typeof timestamp.seconds === 'bigint'
      ? Number(timestamp.seconds)
      : timestamp.seconds
    if (seconds == null || (typeof seconds === 'number' && isNaN(seconds))) {
      return ''
    }
    date = new Date(seconds * 1000)
  }

  if (isNaN(date.getTime())) return ''

  const days = differenceInCalendarDays(date, new Date())
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days > 1) return `in ${days} days`
  if (days === -1) return 'yesterday'
  return `${Math.abs(days)} days ago`
}

/**
 * Formats a protobuf timestamp to a relative time string with full words
 *
 * @param timestamp - Protobuf timestamp object with seconds property, or a Date object
 * @returns Relative time string with full words, or empty string if timestamp is invalid
 *
 * @example
 * formatTimestampRelativeLong({ seconds: 1734567890, nanos: 0 })
 * // Returns: "5 mins ago"
 */
export function formatTimestampRelativeLong(timestamp?: any): string {
  if (!timestamp) return ''

  let date: Date
  if (timestamp instanceof Date) {
    date = timestamp
  } else {
    const seconds = typeof timestamp.seconds === 'bigint'
      ? Number(timestamp.seconds)
      : timestamp.seconds
    if (seconds == null || (typeof seconds === 'number' && isNaN(seconds))) {
      return ''
    }
    date = new Date(seconds * 1000)
  }

  if (isNaN(date.getTime())) return ''
  return formatRelativeTimeLong(date)
}
